import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import axios from 'axios';
import { APIURL } from '../../config';
import moment from 'moment';
import EditShiftModal from '../../container/Scheduler/Modals/EditShiftModal';
import { getSessionAttendance } from './shiftStatus';
import './Alerts.css';

const ALERT_TYPES = {
    ALL: 'all',
    UNASSIGNED: 'unassigned',
    CHECKIN: 'checkin',
    CHECKOUT: 'checkout',
    INCIDENT: 'incident',
};

export default function Alerts(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [alerts, setAlerts] = useState([]);
    const [activeTab, setActiveTab] = useState(ALERT_TYPES.ALL);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [alertData, setAlertData] = useState({
        sessions: [],
        taskTypes: [],
        resources: [],
        employees: [],
        customers: [],
    });

    useEffect(() => {
        fetchAlerts();
    }, []);

    const fetchAlerts = async () => {
        try {
            setIsLoading(true);
            const alertsList = [];
            const now = moment();
            const today = now.format('YYYY-MM-DD');
            const incidentStart = moment().subtract(3, 'days').format('MM/DD/YYYY');
            const incidentEnd = now.format('MM/DD/YYYY');

            // Round 1: independent fetches in parallel — schedules include employee names, sessions lite include cus_name
            const [schedulesRes, subscribedFormsRes] = await Promise.all([
                axios.get(`${APIURL}/master_schedule/company/${props.comp_id}`, {
                    params: { startDate: today, endDate: today },
                }),
                axios.get(`${APIURL}/task_form/subscribed`, { params: { company_id: props.comp_id } })
                    .catch(() => ({ data: [] })),
            ]);

            const todaySchedules = schedulesRes.data || [];
            const incidentForms = (subscribedFormsRes.data || []).filter(
                (f) => f.form_name?.toLowerCase().includes('incident')
            );

            // Round 2: sessions (needs schedule IDs) + task types + resources + all incident form
            //          data fetches — all fired in parallel, no serial loops
            const scheduleIds = todaySchedules.map((s) => s.schedule_id).filter(Boolean);
            const [sessionsRes, taskTypesRes, resourcesRes, ...incidentFormResults] = await Promise.all([
                scheduleIds.length > 0
                    ? axios.post(`${APIURL}/sessions/scheduleLite`, { scheduleIds })
                    : Promise.resolve({ data: [] }),
                axios.get(`${APIURL}/dropdowns/dropdownType/fullTaskType`, {
                    params: { comp_id: props.comp_id },
                }).catch(() => ({ data: [] })),
                axios.get(`${APIURL}/resources/company/${props.comp_id}`).catch(() => ({ data: [] })),
                ...incidentForms.map((form) =>
                    axios.get(`${APIURL}/customer_form/formsWithData/0`, {
                        params: {
                            compID: props.comp_id,
                            formID: form.form_id,
                            taskStartDate: incidentStart,
                            taskEndDate: incidentEnd,
                        },
                    }).catch(() => ({ data: [] }))
                ),
            ]);

            const sessions = sessionsRes.data || [];
            const taskTypes = (taskTypesRes.data || []).map((t) => t.EntityValue);
            const resources = (resourcesRes.data || []).filter((r) => r.resource_is_active);

            setAlertData(prev => ({ ...prev, sessions, taskTypes, resources }));

            // Build schedule-based alerts
            todaySchedules.forEach((schedule) => {
                const scheduleSessions = sessions.filter(
                    (s) => s.schedule_id === schedule.schedule_id
                );
                const shiftStart = moment(schedule.start_time);
                const shiftEnd = moment(schedule.end_time);
                // employee_names comes from the schedule query (STRING_AGG JOIN)
                const empNames = schedule.employee_names || '';
                // cus_name comes from sessions lite (LEFT JOIN customer)
                const custNames = scheduleSessions
                    .map((s) => s.cus_name)
                    .filter(Boolean)
                    .join(', ');

                // Unassigned shift
                if (!schedule.employee_ids || schedule.employee_ids.length === 0) {
                    alertsList.push({
                        id: `unassigned-${schedule.schedule_id}`,
                        type: ALERT_TYPES.UNASSIGNED,
                        severity: 'high',
                        icon: 'fas fa-user-slash',
                        title: 'Unassigned Shift',
                        description: `"${schedule.schedule_name}" at ${shiftStart.format('h:mm A')} has no caregiver assigned.`,
                        employee: '',
                        customer: custNames || '',
                        time: shiftStart.toISOString(),
                        shiftName: schedule.schedule_name,
                        schedule: schedule,
                    });
                }

                // Missing check-in: shift has started but no session is checked in
                if (shiftStart.isSameOrBefore(now) && shiftEnd.isAfter(now)) {
                    const hasCheckin = scheduleSessions.some((s) => getSessionAttendance(s).checkedIn);
                    if (!hasCheckin) {
                        alertsList.push({
                            id: `checkin-${schedule.schedule_id}`,
                            type: ALERT_TYPES.CHECKIN,
                            severity: 'high',
                            icon: 'fas fa-sign-in-alt',
                            title: 'Missing Check-In',
                            description: `"${schedule.schedule_name}" started at ${shiftStart.format('h:mm A')} but no check-in has been recorded.`,
                            employee: empNames || 'Unassigned',
                            customer: custNames || '',
                            time: shiftStart.toISOString(),
                            shiftName: schedule.schedule_name,
                        });
                    }
                }

                // Missing check-out: shift has ended, sessions checked in but not out
                if (shiftEnd.isSameOrBefore(now)) {
                    const missingCheckout = scheduleSessions.filter((s) => {
                        const attendance = getSessionAttendance(s);
                        return attendance.checkedIn && !attendance.checkedOut;
                    });
                    if (missingCheckout.length > 0) {
                        const missingCustNames = missingCheckout
                            .map((s) => s.cus_name)
                            .filter(Boolean)
                            .join(', ');
                        alertsList.push({
                            id: `checkout-${schedule.schedule_id}`,
                            type: ALERT_TYPES.CHECKOUT,
                            severity: 'medium',
                            icon: 'fas fa-sign-out-alt',
                            title: 'Missing Check-Out',
                            description: `"${schedule.schedule_name}" ended at ${shiftEnd.format('h:mm A')} but check-out was not completed.`,
                            employee: empNames || 'Unassigned',
                            customer: missingCustNames || custNames || '',
                            time: shiftEnd.toISOString(),
                            shiftName: schedule.schedule_name,
                        });
                    }
                }
            });

            // Build incident alerts from parallel results (index matches incidentForms order)
            incidentForms.forEach((form, i) => {
                const filedForms = incidentFormResults[i]?.data || [];
                filedForms.forEach((entry, idx) => {
                    const empName = [entry.emp_first_name, entry.emp_last_name]
                        .filter(Boolean)
                        .join(' ');
                    const description =
                        entry.formData?.description ||
                        entry.formData?.NoteText ||
                        entry.formData?.incidentDescription ||
                        `${form.form_name} filed for ${entry.cus_name || 'a customer'}`;
                    alertsList.push({
                        id: `incident-${form.form_id}-${idx}`,
                        type: ALERT_TYPES.INCIDENT,
                        severity: 'high',
                        icon: 'fas fa-file-alt',
                        title: 'Incident Report Filed',
                        description,
                        employee: empName || '',
                        customer: entry.cus_name || '',
                        time: entry.empLocdate || '',
                        shiftName: entry.tas_title || '',
                    });
                });
            });

            // Sort: high severity first, then by time descending
            alertsList.sort((a, b) => {
                const sevOrder = { high: 0, medium: 1, low: 2 };
                if (sevOrder[a.severity] !== sevOrder[b.severity]) {
                    return sevOrder[a.severity] - sevOrder[b.severity];
                }
                return moment(b.time).valueOf() - moment(a.time).valueOf();
            });

            setAlerts(alertsList);
        } catch (error) {
            console.error('Error fetching alerts:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredAlerts =
        activeTab === ALERT_TYPES.ALL
            ? alerts
            : alerts.filter((a) => a.type === activeTab);

    const getCount = (type) =>
        type === ALERT_TYPES.ALL
            ? alerts.length
            : alerts.filter((a) => a.type === type).length;

    const getTagClass = (type) => {
        switch (type) {
            case ALERT_TYPES.UNASSIGNED:
                return 'tag-unassigned';
            case ALERT_TYPES.CHECKIN:
                return 'tag-checkin';
            case ALERT_TYPES.CHECKOUT:
                return 'tag-checkout';
            case ALERT_TYPES.INCIDENT:
                return 'tag-incident';
            default:
                return '';
        }
    };

    const getTagLabel = (type) => {
        switch (type) {
            case ALERT_TYPES.UNASSIGNED:
                return 'Unassigned';
            case ALERT_TYPES.CHECKIN:
                return 'Check-In';
            case ALERT_TYPES.CHECKOUT:
                return 'Check-Out';
            case ALERT_TYPES.INCIDENT:
                return 'Incident';
            default:
                return '';
        }
    };

    if (isLoading) {
        return (
            <div className="alerts-loading">
                <CircularProgress size={32} />
            </div>
        );
    }

    return (
        <div className="alerts-container">
            <div className="alerts-header">
                <div className="alerts-header-left">
                    <i
                        className="fas fa-bell"
                        style={{ color: '#f44336', fontSize: 16 }}
                    ></i>
                    <span className="alerts-header-title">Alerts</span>
                    {alerts.length > 0 && (
                        <span className="alerts-header-count">{alerts.length}</span>
                    )}
                </div>
            </div>

            <div className="alerts-tabs">
                {[
                    { key: ALERT_TYPES.ALL, label: 'All' },
                    { key: ALERT_TYPES.UNASSIGNED, label: 'Unassigned', icon: 'fas fa-user-slash' },
                    { key: ALERT_TYPES.CHECKIN, label: 'No Check-In', icon: 'fas fa-sign-in-alt' },
                    { key: ALERT_TYPES.CHECKOUT, label: 'No Check-Out', icon: 'fas fa-sign-out-alt' },

                    { key: ALERT_TYPES.INCIDENT, label: 'Incidents', icon: 'fas fa-file-alt' },
                ].map((tab) => (
                    <div
                        key={tab.key}
                        className={`alerts-tab ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.icon && <i className={tab.icon} style={{ fontSize: 11 }}></i>}
                        {tab.label}
                        <span className="alerts-tab-badge">{getCount(tab.key)}</span>
                    </div>
                ))}
            </div>

            <div className="alerts-list">
                {filteredAlerts.length === 0 ? (
                    <div className="alerts-empty">
                        <i
                            className="fas fa-check-circle"
                            style={{ fontSize: 28, color: '#4caf50' }}
                        ></i>
                        <span>No alerts</span>
                    </div>
                ) : (
                    filteredAlerts.map((alert) => (
                        <div key={alert.id} className={`alerts-item${alert.type === ALERT_TYPES.UNASSIGNED ? ' alerts-item-clickable' : ''}`}
                            onClick={async () => {
                                if (alert.type === ALERT_TYPES.UNASSIGNED && alert.schedule) {
                                    setSelectedSchedule(alert.schedule);
                                    // Lazy-load employees and customers only when the edit modal opens
                                    try {
                                        const [empRes, cusRes] = await Promise.all([
                                            axios.get(`${APIURL}/employees`, { params: { comp_id: props.comp_id } }),
                                            axios.get(`${APIURL}/customers`, { params: { com_id: props.comp_id } }),
                                        ]);
                                        setAlertData(prev => ({
                                            ...prev,
                                            employees: empRes.data || [],
                                            customers: cusRes.data || [],
                                        }));
                                    } catch (e) {
                                        console.error('Error loading modal data:', e);
                                    }
                                    setEditModalOpen(true);
                                }
                            }}
                        >
                            <div
                                className={`alerts-item-icon severity-${alert.severity}`}
                            >
                                <i className={alert.icon}></i>
                            </div>
                            <div className="alerts-item-content">
                                <div className="alerts-item-title">{alert.title}</div>
                                <div className="alerts-item-desc">
                                    {alert.description}
                                </div>
                                <div className="alerts-item-meta">
                                    <span
                                        className={`alerts-item-tag ${getTagClass(alert.type)}`}
                                    >
                                        {getTagLabel(alert.type)}
                                    </span>
                                    {alert.employee && (
                                        <span className="alerts-item-time">
                                            <i
                                                className="fas fa-user"
                                                style={{ marginRight: 3 }}
                                            ></i>
                                            {alert.employee}
                                        </span>
                                    )}
                                    {alert.customer && (
                                        <span className="alerts-item-time">
                                            <i
                                                className="fas fa-user-injured"
                                                style={{ marginRight: 3 }}
                                            ></i>
                                            {alert.customer}
                                        </span>
                                    )}
                                    {alert.time && (
                                        <span className="alerts-item-time">
                                            {moment(alert.time).format('h:mm A')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {editModalOpen && selectedSchedule && (
                <EditShiftModal
                    open={editModalOpen}
                    onClose={() => { setEditModalOpen(false); setSelectedSchedule(null); }}
                    user={props.user}
                    comp_id={props.comp_id}
                    schedule={selectedSchedule}
                    sessions={alertData.sessions}
                    employees={alertData.employees}
                    customers={alertData.customers}
                    taskTypes={alertData.taskTypes}
                    resources={alertData.resources}
                    isDayCare={false}
                    reloadScheduleData={() => { window.location.reload(); }}
                    reloadSessionData={() => { window.location.reload(); }}
                    onSuccess={(msg) => { setEditModalOpen(false); setSelectedSchedule(null); window.location.reload(); }}
                    onError={(msg) => { console.error(msg); }}
                />
            )}
        </div>
    );
}
